import React, { useState, useContext, useEffect } from 'react';
import { Row, Col, Form, Button, Upload, Space, Typography, message, Spin, Modal } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { createChatBot, uploadFile, updateFile } from 'api-services/data-source';
import { useNavigate } from 'react-router-v6';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';

import ChatBotContext from 'hooks/chat-bot-context';
import '../scss/data-source.scss';
import useLoading from 'hooks/use-loading';
import eventEmitter from 'utilities/event-emitter';
import * as PDFJS from 'pdfjs-dist/build/pdf';
import * as pdfjsWorker from 'pdfjs-dist/build/pdf.worker';

const { Dragger } = Upload;
const { Paragraph } = Typography;
window.PDFJS = PDFJS;

const DraggableUploadListItem = ({ originNode, file, characterCount }) => {
  return (
    <div>
      {file.status === 'error' ? originNode.props.children : originNode}
      {characterCount ? <Paragraph className='file-char-count'>Character count: {characterCount}</Paragraph> : <></>}
    </div>
  );
};

const Document = ({ onEditMode, sourceType, dataSource, allowedDataSize }) => {
  const { loading, setLoading } = useLoading();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { selectedBot } = useContext(ChatBotContext);
  const [fileList, setFileList] = useState([]);
  const [fileInfo, setFileInfo] = useState([]);
  const [characterCount, setCharacterCount] = useState(0);
  const [fileLoader, setFileLoader] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [targetFile, setTargetFile] = useState(null);

  const [initialFile, setInitialFile] = useState(null);
  PDFJS.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const props = {
    name: 'file',
    multiple: true,
    onRemove: (e) => {
      if (fileList[0]?.name && initialFile !== fileList[0].name) {
        setCharacterCount(null);
        setTargetFile(e);
        setIsModalVisible(!isModalVisible);
      } else {
        setFileList([]);
        setFileInfo([]);
      }
    },
  };

  const handleSubmit = async () => {
    if (fileList.length == 0) {
      message.error('Please upload file');
      return;
    }
    try {
      setLoading(true);
      if (onEditMode) {
        const response = await updateFile(selectedBot, {
          files: fileInfo,
          file_type: fileInfo.length > 1 ? 'multi' : sourceType
        });

        if (response?.status === 200) {
          message.success('Chatbot datasource has been updated successfully');
        }
      } else {
        const fileComponents = fileList[0].name.split('.');
        const botName = fileComponents[0];

        const response = await createChatBot({
          files: fileInfo,
          source_type: fileInfo.length > 1 ? 'multi' : sourceType,
          characters_count: characterCount,
          bot_name: botName,
          unique_filename: fileInfo[0].unique_filename,
        });

        if (response?.status === 200) {
          message.success('Chatbot has been created successfully');
          const { id, characters_count: charsCount, source_type: sourceType } = response.data;
          eventEmitter.emit('botSelected', { id: id, bot_name: botName, bot_source_type: sourceType, characters_count: charsCount, 
            source_type: sourceType, message_count: 0 });
          setLoading(false);
          navigate('/embed-on-site');
        }
      }
    } catch (e) {
      setLoading(false);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const getCharactersCount = (file) =>{
    return new Promise((res) => {
      const filenameParts = file.name.split('.');
      const fileType = filenameParts[filenameParts.length - 1];
      const reader = new FileReader();
      switch(fileType) {
      case 'txt':
        reader.onload = () => {
          const fileText = reader.result;
          res(fileText.length);
        };
        reader.readAsText(file);
        break;
      case 'pdf':
        reader.onload = () => {
          const pdfBuffer = new Uint8Array(reader.result);
          PDFJS.getDocument(pdfBuffer).promise.then((pdf) => {
            const totalPages = pdf._pdfInfo.numPages;
            const pagePromises = [];
            for (let i = 1; i <= totalPages; i++) {
              const page = pdf.getPage(i);
              pagePromises.push(page.then((page) => {
                const textContent = page.getTextContent();
                return textContent.then((text) => {
                  return text.items.map((s) =>  s.str).join('');
                });
              }));
            }
            Promise.all(pagePromises).then(textList => {
              const fileText = textList.join('');
              res(fileText.length);
            });
          });
        };
        reader.readAsArrayBuffer(file);
        break;
      case 'docx':
        reader.onload = () => {
          const zip = new PizZip(reader.result);
          const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
          });
          const fileText = doc.getFullText();
          res(fileText.length);
        };
        reader.readAsBinaryString(file);
        break;
      }
    });
  };
  
  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      setLoading(true);
      if (fileList) {
        const charactersCount = await getCharactersCount(file);
        
        if((characterCount + charactersCount) > allowedDataSize) {
          message.info(
            `Data size excceded allowed limit. Limit is ${allowedDataSize} while data size is ${characterCount + charactersCount}.`,
          );
          return;
        }
        const fileType = file.name.split('.');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_type', fileType[fileType.length - 1]);

        setFileLoader(true);
        const response = await uploadFile(formData);
        setFileLoader(false);

        if (response.status == 200) {
          setCharacterCount(prevCount => prevCount + parseInt(response.data.characters_count));
          setFileInfo(prevFileInfo => [...prevFileInfo, {
            uid: file.uid,
            file_path: response.data.file_path,
            file_type: fileType[fileType.length - 1],
            filename: response.data.filename,
            unique_filename: response.data.unique_filename,
            characters_count: response.data.characters_count,
          }]);
          setFileList(prevFileList => [...prevFileList, file]);
          message.info(
            `File uploaded successfully. Please click the ${onEditMode ? 'Update' : 'Create'} Chatbot button to ${
              onEditMode ? 'update your chatbot' : 'start creating'
            } your chatbot.`,
          );
          onSuccess();
        } else {
          message.error(response.response?.data?.error);
          onError();
        }
      } else {
        message.warning('You can only upload one file at a time.');
      }
    } catch (e) {
      onError();
      message.error('Failed to upload file');
      console.log(e);
    } finally {
      setLoading(false);
    }
  };
  /*
  const customRequestEdit = async ({ file, onSuccess, onError }) => {
    try {
      setLoading(true);
      const charactersCount = await getCharactersCount(file);
      if(charactersCount > allowedDataSize) {
        message.info(
          `Data size exceeded allowed limit. Limit is ${allowedDataSize} while data size is ${charactersCount}.`,
        );
        return;
      }
      setFileList([file]);
      onSuccess();
    } catch (e) {
      onError();
      message.error('Failed to upload file');
      console.log(e);
    } finally {
      setLoading(false);
    }
  };
  */

  const removeFile = () => {
    setFileList(fileList.filter((file) => file.uid !== targetFile.uid));
    const f = fileInfo.find((file) => file.uid === targetFile.uid);
    console.log(f);
    setFileInfo(fileInfo.filter((file) => file.uid !== targetFile.uid));
    setIsModalVisible(!isModalVisible);
  };

  const handleCancel = () => {
    setIsModalVisible(!isModalVisible);
  };

  useEffect(() => {
    if (onEditMode && dataSource) {
      if (sourceType === 'document') {
        setInitialFile(dataSource.filename);
        const files = dataSource.files.map((file) => { 
          file.name = file.filename;
          return file;
        });
        setFileList(files);
        setFileInfo(files);
      }
    }
  }, [dataSource, onEditMode]);

  return (
    <>
      <Row className='upload-file-wrapper'>
        <Col span={24}>
          <Form id='upload-document' form={form} layout='vertical'>
            <Space size='middle' direction='vertical'>
              <Dragger
                accept='.pdf, .doc, .docx, .txt'
                {...props}
                multiple={true}
                fileList={fileList}
                customRequest={
                  customRequest
                }
                showUploadList={{ showRemoveIcon: true }}
                beforeUpload={(file) => {
                  const splitted = file.name.split('.');
                  const fileType = splitted[splitted.length - 1];

                  if (fileType != 'pdf' && fileType != 'doc' && fileType != 'docx' && fileType != 'txt') {
                    message.error('Supported file formats are .txt, .pdf, .doc, .docx');
                    return false;
                  }
                }}
                itemRender={(originNode, file) => (
                  <DraggableUploadListItem
                    className='no-delete'
                    originNode={originNode}
                    file={file}
                    characterCount={fileInfo.find((f) => f.uid === file.uid)?.characters_count || null}
                  />
                )}>
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined />
                </p>
                <p className='ant-upload-text'>Click or drag file to this area to upload</p>
                <p className='ant-upload-hint'>
                Supported file formats are .txt, .pdf, .doc, .docx
                </p>
              </Dragger>
              {fileLoader ? <Spin size='medium' /> : <></>}
              <div className='create-chatbot-wrapper'>
                <Form.Item>
                  <Button loading={loading} type='primary' onClick={handleSubmit}>
                    {onEditMode ? 'Update' : 'Create'} Chatbot
                  </Button>
                </Form.Item>
              </div>
            </Space>
          </Form>
        </Col>
        <Modal title='Confirmation' open={isModalVisible} onOk={removeFile} onCancel={handleCancel}>
          <p>Are you sure , you want to delete file.</p>
        </Modal>
      </Row>
    </>
  );
};

export default Document;
